import React from 'react';
import { Booking } from '../types/booking.types';

interface PaymentSummaryCardProps {
  booking: Booking;
  additionalTotals?: {
    subtotal: number;
    cgst: number;
    sgst: number;
    total: number;
  };
}

const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({ booking, additionalTotals }) => {
  const bookingData = booking as Booking & Record<string, unknown>;

  const toAmount = (value: unknown): number => {
    const parsed = parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatAppliedLabel = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const displayFields = [
        obj.title,
        obj.name,
        obj.label,
        obj.offer_title,
        obj.offer_name,
        obj.coupon_code,
        obj.code,
      ];
      const firstText = displayFields.find((field) => typeof field === 'string' && field.trim().length > 0);
      if (typeof firstText === 'string') return firstText.trim();
      if (obj.id !== undefined && obj.id !== null) return `#${obj.id}`;
    }
    return '';
  };

  const toAppliedList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map(formatAppliedLabel)
        .filter((item): item is string => Boolean(item));
    }
    const single = formatAppliedLabel(value);
    return single ? [single] : [];
  };

  const appliedCoupon =
    formatAppliedLabel(bookingData.applied_coupon) ||
    formatAppliedLabel(bookingData.coupon_code) ||
    formatAppliedLabel(bookingData.appliedCoupon) ||
    formatAppliedLabel(bookingData.applied_coupon_code);

  const appliedOffers = [
    ...toAppliedList(bookingData.applied_offers),
    ...toAppliedList(bookingData.offers),
    ...toAppliedList(bookingData.applied_offer),
    ...toAppliedList(bookingData.offer),
    ...toAppliedList(bookingData.service_offers),
  ].filter((offer, index, arr) => arr.indexOf(offer) === index);

  const additionalSubtotal = additionalTotals?.subtotal ?? 0;
  const additionalCgst = additionalTotals?.cgst ?? 0;
  const additionalSgst = additionalTotals?.sgst ?? 0;
  const additionalTotal = additionalTotals?.total ?? 0;

  const baseFinal = parseFloat(String(booking.final_amount || 0));
  const baseDue = parseFloat(String(booking.amount_due || 0));
  const grandTotal = baseFinal + additionalTotal;
  const dueAfterAdditional = baseDue + additionalTotal;

  const totalCouponDiscount = toAmount(bookingData.total_coupon_discount);
  const totalOfferDiscount = toAmount(bookingData.total_offer_discount);
  const totalDiscount = toAmount(bookingData.total_discount);
  const adjustmentAmount = toAmount(bookingData.adjustments_total);

  return (
    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
      <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center">
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        Payment Summary
      </h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotal:</span>
          <span className="font-semibold">₹{parseFloat(String(booking.subtotal_amount || 0)).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">CGST ({booking.cgst_rate}%):</span>
          <span className="font-semibold">₹{parseFloat(String(booking.cgst || 0)).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">SGST ({booking.sgst_rate}%):</span>
          <span className="font-semibold">₹{parseFloat(String(booking.sgst || 0)).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">total_coupon_discount:</span>
          <span className="font-semibold text-red-600">-₹{Math.abs(totalCouponDiscount).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">total_offer_discount:</span>
          <span className="font-semibold text-red-600">-₹{Math.abs(totalOfferDiscount).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">total_discount:</span>
          <span className="font-semibold text-red-600">-₹{Math.abs(totalDiscount).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Adjustment:</span>
          <span
            className={`font-semibold ${adjustmentAmount < 0 ? 'text-red-600' : adjustmentAmount > 0 ? 'text-green-600' : ''}`}
          >
            {adjustmentAmount < 0 ? '-' : adjustmentAmount > 0 ? '+' : ''}₹{Math.abs(adjustmentAmount).toFixed(2)}
          </span>
        </div>
        {(appliedCoupon || appliedOffers.length > 0) && (
          <div className="border-t border-purple-100 pt-2 mt-2 space-y-1">
            {appliedCoupon && (
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">Coupon Used:</span>
                <span className="font-semibold text-right">{appliedCoupon}</span>
              </div>
            )}
            {appliedOffers.length > 0 && (
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">Offer Used:</span>
                <span className="font-semibold text-right">{appliedOffers.join(', ')}</span>
              </div>
            )}
          </div>
        )}
        <div className="border-t border-purple-200 pt-2 mt-2">
          <div className="flex justify-between">
            <span className="font-bold text-purple-800">Total Amount:</span>
            <span className="font-bold text-purple-800">₹{parseFloat(String(booking.final_amount || 0)).toFixed(2)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-600">Paid:</span>
            <span className="font-semibold text-green-600">₹{parseFloat(String(booking.paid_amount || 0)).toFixed(2)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-600 font-medium">Due:</span>
            <span className="font-bold text-red-600">₹{parseFloat(String(booking.amount_due || 0)).toFixed(2)}</span>
          </div>
        </div>

        {additionalTotal > 0 && (
          <div className="border-t border-purple-200 pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Additional Subtotal:</span>
              <span className="font-semibold">₹{additionalSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Additional CGST:</span>
              <span className="font-semibold">₹{additionalCgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Additional SGST:</span>
              <span className="font-semibold">₹{additionalSgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="font-semibold text-purple-800">Additional Total:</span>
              <span className="font-semibold text-purple-800">₹{additionalTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mt-2 border-t border-purple-100 pt-2">
              <span className="font-bold text-purple-800">Grand Total:</span>
              <span className="font-bold text-purple-800">₹{grandTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600 font-medium">Due After Additional:</span>
              <span className="font-bold text-red-600">₹{dueAfterAdditional.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSummaryCard;
